import { OrderedMap } from 'immutable';
import { Observable } from 'rxjs';
import queryString from 'query-string';

import {
  BlockOrganizerUserInOrganizationDocument,
  SwitchOrganizationDocument,
  UnblockOrganizerUserInOrganizationDocument,
} from 'generated/graphql';

import Api from '../api';
import { mutate } from '../graphql';

import Organizer from './model';
import { addMultipleAction, addAction, removeAction, setTotalAction } from './actions';

export default class OrganizersApi {
  static fetchRaw(organization, query = {}) {
    const queryStr = queryString.stringify(query);
    return Api.get(`api/organizations/${organization.id}/organizers?${queryStr}`);
  }

  static fetch(organization, query) {
    return OrganizersApi.fetchRaw(organization, query).map((result) => [
      addMultipleAction(
        OrderedMap(
          result.organizers && result.organizers.map((user) => [user.id, new Organizer(user)])
        )
      ),
      setTotalAction(result.total),
    ]);
  }

  static fetchPopulatedOrganizerUser(id) {
    return Api.get(`api/users/getPopulatedOrganizerUser/${id}`);
  }

  static fetchById(id) {
    return Api.get(`api/users/showOrganizer/${id}`).map((user) => addAction(new Organizer(user)));
  }

  static create(user) {
    return Api.post('api/users/createOrganizerForMyOrg', user).map((persistedUser) =>
      addAction(new Organizer(persistedUser))
    );
  }

  static update(id, data) {
    const user = { ...data };
    delete user.privileges;
    return Api.patch(`api/users/${id}`, user).map((updatedUser) =>
      addAction(new Organizer(updatedUser))
    );
  }

  static delete(id) {
    return Api.delete(`api/users/${id}`).map(() => removeAction(id));
  }

  /**
   * Unblock an organizer user within the current admin's organization.
   * - If they have a `Blocked` role, swap it out with `Administrator`
   * - If they have existing roleGrants for this current organization, just re-enable them
   * - If they have no roles, just grant an `Administrator` role
   * - If they are `provider:'blocked'`, set them to `provider: 'local'`
   */
  static unblock(userId) {
    return Observable.from(mutate(UnblockOrganizerUserInOrganizationDocument, { userId })).map(
      ({ errors, data }) => {
        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (!data) {
          throw new Error('Error unblocking user');
        }

        return true;
      }
    );
  }

  /**
   * Take all the roleGrants for the current admin's org for the user and set them as `enabled: false`
   */
  static block(userId) {
    return Observable.from(mutate(BlockOrganizerUserInOrganizationDocument, { userId })).map(
      ({ errors, data }) => {
        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (!data) {
          throw new Error('Error blocking user');
        }

        return true;
      }
    );
  }

  /**
   * Switch active organization of the current organizer user
   */
  static switchOrganization(organizationId) {
    return Observable.from(mutate(SwitchOrganizationDocument, { organizationId })).map(
      ({ errors, data }) => {
        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (!data) {
          throw new Error('Error switching organization');
        }

        return true;
      }
    );
  }
}
